import { ComponentProps, useContext } from 'react';
import { Button, Classes, Tooltip } from '@blueprintjs/core';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import { ace_hooks } from '@axo/shared/data-access/hooks';
import { ace } from '@axo/shared/data-access/types';
import { classNames } from '@axo/shared/util/dom';
import { AceTable } from '../AceTable';
import { SmallTag } from '../SmallTag';
import { ApplicationLink } from '../ApplicationLink';
import { BucketName } from '../BucketName';
import { ConditionSummary } from './ConditionSummary';
import styles from './WorkQueue.module.css';

interface Props {
  marketCountry: string;
}

export function WorkQueue({ marketCountry }: Props) {
  const { state } = useContext(DataAccessContext);
  const queue = ace_hooks.useWorkQueue(state.user.clientID, marketCountry);

  return (
    <>
      <div className={styles.header}>
        <div className={styles.actions}>
          <SmallTag minimal intent="primary" icon="eye-open" round>
            <span className={classNames(queue.isLoading && Classes.SKELETON)}>
              Total {`${queue.data?.length ?? '-'}`}
            </span>
          </SmallTag>

          <Tooltip content="Refresh results">
            <Button
              icon="refresh"
              loading={queue.isFetching}
              disabled={queue.isIdle}
              variant="minimal"
              style={{ marginLeft: 'var(--margin-xsmall)' }}
              onClick={() => {
                queue.refetch();
              }}
            />
          </Tooltip>
        </div>
      </div>

      <AceTable<ace.ApplicationBucket>
        config={listConfig}
        uniqueRowIDKey="LoanApplicationID"
        loading={queue.isLoading}
        data={queue.data ?? []}
      />
    </>
  );
}

const listConfig: ComponentProps<
  typeof AceTable<ace.ApplicationBucket>
>['config'] = {
  columns: {
    LoanApplicationID: {
      label: 'Application',
      render: (_, app) => (
        <ApplicationLink id={`${app.LoanApplicationID ?? ''}`} />
      ),
    },
  },
  extra: [
    {
      label: 'Bucket name',
      key: 'bucket-name',
      render: (item) => <BucketName id={item.BucketID} />,
    },
    {
      label: 'Condition summary',
      key: 'condition-summary',
      render: (item) => <ConditionSummary id={item.BucketID} />,
    },

    // Added placeholder to make sure last column is left aligned.
    {
      label: '',
      key: 'placeholder',
      render: () => null,
    },
  ],
};
